/**
 * PatientVerification - Primer paso del wizard
 * Verificación de paciente existente vs nuevo
 */

import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useBooking } from '../BookingContext';
import { loadRut, saveRut, clearProgressForNewRut } from '../bookingPersistence';
import PatientValidationService, { RATE_LIMIT_MESSAGE } from '../../../services/patient-validation.service';
import type { PatientValidationResult } from '../../../services/patient-validation.service';

interface PatientVerificationProps {
  onNext: () => void;
  onExistingPatient?: () => void;
}

const PatientVerification: React.FC<PatientVerificationProps> = ({ onNext, onExistingPatient }) => {
  const { state, setPatientVerification, setError } = useBooking();
  // Prefill del RUT desde sessionStorage (UX_VISION P1-5 — antes vivía en localStorage
  // y no se limpiaba nunca; `loadRut` aplica TTL y `bookingPersistence` lo migra).
  const [rutInput, setRutInput] = useState<string>(() => loadRut()?.rut ?? '');
  const [isSearching, setIsSearching] = useState(false);
  const [showNewPatientForm, setShowNewPatientForm] = useState(false);
  // Paciente bloqueado administrativamente por la clínica: pantalla sin salida,
  // no bifurca a existente ni a nuevo. El enforcement real es server-side
  // (trigger trg_consultas_rechaza_paciente_bloqueado) — esto es sólo el aviso.
  const [isBlocked, setIsBlocked] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [isDbError, setIsDbError] = useState(false);
  const [isExtranjero, setIsExtranjero] = useState(false);
  const [documentoInput, setDocumentoInput] = useState('');
  const rutInputRef = useRef<HTMLInputElement>(null);

  // Formatear RUT mientras se escribe
  const formatRUT = (value: string): string => {
    // Remover caracteres no válidos
    const cleanValue = value.replace(/[^0-9kK]/g, '');
    
    // Aplicar formato XX.XXX.XXX-X
    if (cleanValue.length <= 1) return cleanValue;
    if (cleanValue.length <= 4) return cleanValue.replace(/(\d{1,3})/, '$1');
    if (cleanValue.length <= 7) return cleanValue.replace(/(\d{1,2})(\d{1,3})/, '$1.$2');
    return cleanValue.replace(/(\d{1,2})(\d{3})(\d{3})(\w{1})/, '$1.$2.$3-$4');
  };

  // Validar RUT completo (formato + dígito verificador módulo 11).
  // searchPatientByRUT ya revalida esto server-side antes de tocar la BD,
  // pero validar acá evita el round-trip innecesario y da feedback instantáneo.
  const validateRUT = (rut: string): boolean => {
    return PatientValidationService.validateRUT(rut) === null;
  };

  // Búsqueda de paciente en Supabase — retorna el resultado completo para manejo granular de errores
  const searchPatient = async (rut: string): Promise<PatientValidationResult> => {
    try {
      return await PatientValidationService.searchPatientByRUT(rut);
    } catch (error) {
      console.error('Error inesperado al buscar paciente');
      return {
        isExisting: false,
        dbError: true,
        error: 'No pudimos verificar tu RUT. Por favor intentá de nuevo.'
      };
    }
  };

  const handleRUTChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatRUT(e.target.value);
    setRutInput(formattedValue);
    setLocalError(null);
    setIsDbError(false);
    setError(null);
  };

  const handleDocumentoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDocumentoInput(e.target.value);
    setLocalError(null);
    setError(null);
  };

  const handleToggleExtranjero = () => {
    setIsExtranjero(v => !v);
    setDocumentoInput('');
    setRutInput('');
    setLocalError(null);
    setIsDbError(false);
    setError(null);
  };

  const handleSearchForeignDoc = () => {
    const error = PatientValidationService.validateForeignDocument(documentoInput);
    if (error) {
      setLocalError(error);
      return;
    }
    const documento = PatientValidationService.normalizeForeignDocument(documentoInput);
    clearProgressForNewRut(documento); // otro documento = progreso guardado ajeno
    saveRut(documento, true);
    setPatientVerification({
      rut: documento,
      isExisting: false,
      existingPatient: null,
      isExtranjero: true,
    });
    setShowNewPatientForm(true);
  };

  const handleSearch = async () => {
    if (isExtranjero) {
      handleSearchForeignDoc();
      return;
    }
    const rutError = PatientValidationService.validateRUT(rutInput);
    if (rutError) {
      setLocalError(rutError);
      setIsDbError(false);
      return;
    }

    setIsSearching(true);
    setLocalError(null);
    setIsDbError(false);
    setError(null);

    try {
      // Chequeo de bloqueo antes de buscar la ficha: si el paciente está
      // bloqueado por la clínica no debe ver ni el flujo de existente ni el de
      // nuevo, sólo el aviso.
      if (await PatientValidationService.isRutBlocked(rutInput)) {
        setIsBlocked(true);
        return;
      }

      const result = await searchPatient(rutInput);

      // SEC-059: rate-limit server-side — mensaje propio, sin botón de
      // reintento (reintentar ya no ayuda, hay que esperar la ventana).
      if (result.rateLimited) {
        setLocalError(result.error ?? RATE_LIMIT_MESSAGE);
        setIsDbError(false);
        return;
      }

      // Error de BD — no continuar, pedir reintento
      if (result.dbError) {
        setLocalError('No pudimos verificar tu RUT. Por favor intentá de nuevo.');
        setIsDbError(true);
        return;
      }

      // RUT con dígito verificador incorrecto
      if (result.error && result.error.includes('dígito verificador')) {
        setLocalError('El RUT ingresado no es válido. Verificá el dígito verificador.');
        setIsDbError(false);
        return;
      }

      // Cualquier otro error de validación de formato
      if (result.error) {
        setLocalError(result.error);
        setIsDbError(false);
        return;
      }

      if (result.isExisting && result.patient) {
        // Paciente existente encontrado
        clearProgressForNewRut(rutInput);
        saveRut(rutInput, false);
        setPatientVerification({
          rut: rutInput,
          isExisting: true,
          existingPatient: result.patient
        });

        if (onExistingPatient) {
          setTimeout(() => onExistingPatient(), 500);
        }
        return;
      }

      // Paciente no encontrado — flujo nuevo paciente
      clearProgressForNewRut(rutInput);
      saveRut(rutInput, false);
      setPatientVerification({
        rut: rutInput,
        isExisting: false,
        existingPatient: null
      });
      setShowNewPatientForm(true);
    } finally {
      setIsSearching(false);
    }
  };

  const handleContinueAsExisting = () => {
    onNext();
  };

  const handleContinueAsNew = () => {
    setShowNewPatientForm(false);
    onNext();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleResetFromBlocked = () => {
    setIsBlocked(false);
    setRutInput('');
    setLocalError(null);
    setIsDbError(false);
    setError(null);
  };

  return (
    <div className="patient-verification">
      <div className="step-content">
        <AnimatePresence mode="wait">
          {isBlocked && (
            <motion.div
              key="blocked-notice"
              className="result-card result-card--notice"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              role="status"
            >
              <div className="result-card-header">
                <div className="notice-icon-sm" aria-hidden="true">
                  <i className="fas fa-info-circle"></i>
                </div>
                <div>
                  <h2 className="result-card-title">Tu proceso en Conectemos Chile se encuentra cerrado</h2>
                  <p className="result-card-subtitle">
                    No es posible agendar horas por este medio. Si tenés dudas, comunicate
                    con el equipo de Conectemos Chile al <a href="tel:+56978009119">+56 9 7800 9119</a>.
                  </p>
                </div>
              </div>

              <button
                className="btn bw-btn btn-secondary bw-btn-secondary"
                onClick={handleResetFromBlocked}
              >
                <i className="fas fa-arrow-left" aria-hidden="true"></i>
                Ingresar otro RUT
              </button>
            </motion.div>
          )}

          {!isBlocked && !state.patientVerification && !showNewPatientForm && (
            <motion.div
              key="search-form"
              className="verification-card"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              {/* Encabezado integrado — estilo paso 3 */}
              <div className="verification-card-header">
                <div className="pv-brand-mark" aria-hidden="true">
                  <img src="/assets/img/logo.png" alt="" className="pv-brand-mark__img" />
                </div>
                <h2 className="verification-card-title">
                  {isExtranjero ? 'Ingresa tu documento' : 'Ingresa tu RUT'}
                </h2>
                <p className="verification-card-subtitle">
                  {isExtranjero
                    ? 'Ingresá tu DNI, pasaporte u otro documento de identidad'
                    : 'Verificaremos si ya eres paciente del consultorio'}
                </p>
              </div>

              <div className="pv-verification-form">
                <div className="pv-form-group">
                  <label className="pv-form-label" htmlFor="rut-input">
                    {isExtranjero ? 'Número de documento' : 'RUT'}
                  </label>
                  <div className="input-group bw-input-group">
                    {isExtranjero ? (
                      <input
                        id="rut-input"
                        type="text"
                        className={`pv-form-input rut-input ${state.error ? 'error' : ''}`}
                        placeholder="DNI · Pasaporte · Cédula"
                        value={documentoInput}
                        onChange={handleDocumentoChange}
                        onKeyPress={handleKeyPress}
                        maxLength={15}
                        disabled={isSearching}
                        autoComplete="off"
                      />
                    ) : (
                      <input
                        ref={rutInputRef}
                        id="rut-input"
                        type="text"
                        className={`pv-form-input rut-input ${state.error ? 'error' : ''}`}
                        placeholder="12.345.678-9"
                        value={rutInput}
                        onChange={handleRUTChange}
                        onKeyPress={handleKeyPress}
                        maxLength={12}
                        disabled={isSearching}
                      />
                    )}
                    <button
                      className="btn bw-btn btn-primary search-btn"
                      onClick={handleSearch}
                      disabled={isSearching || (isExtranjero ? PatientValidationService.normalizeForeignDocument(documentoInput).length < 6 : !validateRUT(rutInput))}
                    >
                      {isSearching ? (
                        <>
                          <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
                          Buscando...
                        </>
                      ) : (
                        <>
                          <i className={`fas ${isExtranjero ? 'fa-arrow-right' : 'fa-search'}`} aria-hidden="true"></i>
                          {isExtranjero ? 'Continuar' : 'Buscar'}
                        </>
                      )}
                    </button>
                  </div>

                  {/* Toggle extranjero */}
                  <button
                    type="button"
                    className={`foreign-toggle${isExtranjero ? ' active' : ''}`}
                    onClick={handleToggleExtranjero}
                    aria-pressed={isExtranjero}
                  >
                    <i className={`fas ${isExtranjero ? 'fa-toggle-on' : 'fa-toggle-off'}`} aria-hidden="true"></i>
                    {isExtranjero ? 'Tengo RUT chileno' : 'Soy extranjero / No tengo RUT chileno'}
                  </button>

                  {localError && (
                    <motion.div
                      className={`pv-error-message${isDbError ? ' pv-error-message--db' : ''}`}
                      role="alert"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.2 }}
                    >
                      <i className={`fas ${isDbError ? 'fa-wifi' : 'fa-exclamation-triangle'}`} aria-hidden="true"></i>
                      <span>{localError}</span>
                      {isDbError && (
                        <button
                          className="retry-btn"
                          onClick={handleSearch}
                          disabled={isSearching}
                        >
                          <i className="fas fa-redo-alt" aria-hidden="true"></i>
                          Reintentar
                        </button>
                      )}
                    </motion.div>
                  )}
                </div>
              </div>

              {/* Ayuda compacta — una sola línea */}
              {!isExtranjero && (
                <p className="verification-help-inline">
                  <i className="fas fa-info-circle" aria-hidden="true"></i>
                  Problemas: <a href="tel:+56978009119">+56 9 7800 9119</a>
                </p>
              )}
            </motion.div>
          )}

          {state.patientVerification?.isExisting && (
            <motion.div
              key="existing-patient"
              className="result-card result-card--success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.4 }}
            >
              <div className="result-card-header">
                <h2 className="result-card-title result-card-title--success">
                  <i className="fas fa-check-circle" aria-hidden="true"></i>
                  ¡Paciente Encontrado!
                </h2>
              </div>

              <div className="patient-info bw-patient-info">
                <div className="patient-avatar patient-avatar--sm" aria-hidden="true">
                  <i className="fas fa-user"></i>
                </div>
                <div className="patient-details">
                  <h3 className="patient-name bw-patient-name">
                    {state.patientVerification.existingPatient?.nombre} {state.patientVerification.existingPatient?.apellido}
                  </h3>
                  <div className="patient-contact">
                    {/* SEC-057: search_patient_by_rut ya no devuelve email/telefono
                        (filtraban contacto completo por RUT adivinado, sin login).
                        Se vuelven a pedir en ExistingPatientBooking.tsx antes de
                        agendar/cancelar/reagendar — acá solo queda el RUT, que el
                        propio paciente tipeó para llegar a esta pantalla. */}
                    <span className="contact-item bw-contact-item">
                      <i className="fas fa-id-card" aria-hidden="true"></i>
                      {state.patientVerification.existingPatient?.rut}
                    </span>
                  </div>
                </div>
              </div>

              <button
                className="btn bw-btn btn-success"
                onClick={handleContinueAsExisting}
              >
                <i className="fas fa-arrow-right" aria-hidden="true"></i>
                Continuar con esta información
              </button>
            </motion.div>
          )}

          {showNewPatientForm && (
            <motion.div
              key="new-patient"
              className="result-card result-card--info"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.4 }}
            >
              <div className="result-card-header">
                <div className="welcome-icon-sm" aria-hidden="true">
                  <i className="fas fa-heart"></i>
                </div>
                <div>
                  <h2 className="result-card-title">¡Bienvenido a Conectemos Chile!</h2>
                  <p className="result-card-subtitle">
                    Completarás tu ficha de paciente en el siguiente paso.
                  </p>
                </div>
              </div>

              {/* Benefits — chips compactos */}
              <ul className="benefit-chips" aria-label="Beneficios">
                <li className="benefit-chip">
                  <i className="fas fa-user-md" aria-hidden="true"></i>
                  Profesionales especializados
                </li>
                <li className="benefit-chip">
                  <i className="fas fa-shield-alt" aria-hidden="true"></i>
                  Confidencialidad garantizada
                </li>
                <li className="benefit-chip">
                  <i className="fas fa-calendar-check" aria-hidden="true"></i>
                  Horarios flexibles
                </li>
                <li className="benefit-chip">
                  <i className="fas fa-laptop" aria-hidden="true"></i>
                  Presencial y online
                </li>
              </ul>

              <button
                className="btn bw-btn btn-primary"
                onClick={handleContinueAsNew}
              >
                <i className="fas fa-arrow-right" aria-hidden="true"></i>
                Continuar como paciente nuevo
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default PatientVerification;